// import { Resume } from "lib/redux/types";
import { ResumeAnalysis } from "./resume-analyse-types";
import { analyseAwards } from "./section-analysis/analyseAwards";
import { analyseCertificates } from "./section-analysis/analyseCertificates";
import { analyseCustom } from "./section-analysis/analyseCustom";
import { analyseEducations } from "./section-analysis/analyseEducations";
import { analyseLanguages } from "./section-analysis/analyseLanguages";
import { analyseOverallResume } from "./section-analysis/analyseOverallResume";
import { analyseProfile } from "./section-analysis/analyseProfile";
import { analyseProjects } from "./section-analysis/analyseProjects";
import { analyseSkills } from "./section-analysis/analyseSkills";
import { analyseWorkExperiences } from "./section-analysis/analyseWorkExperiences";

const analyseResumeForScoring = async (
  resume: ResumeAnalysis
): Promise<ResumeAnalysis> => {
  const analysed = {
    ...resume,
    profile: analyseProfile(resume?.profile),
    workExperiences: analyseWorkExperiences(resume?.workExperiences),
    projects: analyseProjects(resume?.projects),
    educations: analyseEducations(resume?.educations),
    certifications: analyseCertificates(resume?.certifications),
    languages: analyseLanguages(resume?.languages),
    awards: analyseAwards(resume?.awards),
    skills: analyseSkills(resume?.skills),
    custom: analyseCustom(resume?.custom),
    overallAnalysis: analyseOverallResume(resume, resume?.overallAnalysis),
  };

  // Sanitize analysed resume by removing completely-empty blocks from section arrays.
  // Why: the scoring functions treat an item with empty strings as a present item and
  // penalize missing fields. If the API returns placeholder entries (all-empty strings
  // and no statements), it's usually better to treat them as "absent" so scoring
  // behaves intuitively.
  const sanitizeArray = <T extends { [k: string]: any }>(arr?: T[], predicate?: (item: T) => boolean) => {
    if (!arr || !Array.isArray(arr)) return [] as T[];
    return arr.filter((it) => (predicate ? predicate(it) : true));
  };

  const hasNonEmptyStatement = (desc: any) => !!(desc && desc.statement && String(desc.statement).trim());

  const isMeaningfulProject = (p: any) => {
    if (!p) return false;
    if (p.project && String(p.project).trim()) return true;
    if (p.projectLink && String(p.projectLink).trim()) return true;
    if (p.startDate || p.endDate) return true;
    if (Array.isArray(p.descriptionAnalysis) && p.descriptionAnalysis.some(hasNonEmptyStatement)) return true;
    return false;
  };

  const isMeaningfulExperience = (e: any) => {
    if (!e) return false;
    if (e.company && String(e.company).trim()) return true;
    if (e.role && String(e.role).trim()) return true;
    if (e.startDate || e.endDate) return true;
    if (Array.isArray(e.descriptionAnalysis) && e.descriptionAnalysis.some(hasNonEmptyStatement)) return true;
    return false;
  };

  const isMeaningfulEducation = (ed: any) => {
    if (!ed) return false;
    if (ed.school && String(ed.school).trim()) return true;
    if (ed.degree && String(ed.degree).trim()) return true;
    if (ed.startDate || ed.endDate) return true;
    if (Array.isArray(ed.descriptionAnalysis) && ed.descriptionAnalysis.some(hasNonEmptyStatement)) return true;
    return false;
  };

  const isMeaningfulCertificate = (c: any) => {
    if (!c) return false;
    if (c.certificate && String(c.certificate).trim()) return true;
    if (c.certificateLink && String(c.certificateLink).trim()) return true;
    if (Array.isArray(c.descriptionAnalysis) && c.descriptionAnalysis.some(hasNonEmptyStatement)) return true;
    return false;
  };

  const isMeaningfulLanguage = (l: any) => {
    if (!l) return false;
    if (l.language && String(l.language).trim()) return true;
    if (l.level && String(l.level).trim()) return true;
    if (Array.isArray(l.descriptionAnalysis) && l.descriptionAnalysis.some(hasNonEmptyStatement)) return true;
    return false;
  };

  const isMeaningfulAward = (a: any) => {
    if (!a) return false;
    if (a.award && String(a.award).trim()) return true;
    if (a.issuer && String(a.issuer).trim()) return true;
    if (Array.isArray(a.descriptionAnalysis) && a.descriptionAnalysis.some(hasNonEmptyStatement)) return true;
    return false;
  };

  const isMeaningfulProfile = (p: any) => {
    if (!p) return false;
    if (p.name && String(p.name).trim()) return true;
    if (p.email && String(p.email).trim()) return true;
    if (p.phone && String(p.phone).trim()) return true;
    if (p.location && String(p.location).trim()) return true;
    if (p.summaryAnalysis && p.summaryAnalysis.statement && String(p.summaryAnalysis.statement).trim()) return true;
    return false;
  };

  const sanitized = {
    ...analysed,
    // Keep the profile object shape even when it's not meaningful to avoid
    // surprising runtime behavior in other parts of the app that expect
    // `profile` to exist. Provide a safe, empty `summaryAnalysis.statement`
    // field so consumers can read it without guarding for undefined.
    profile: isMeaningfulProfile(analysed.profile)
      ? analysed.profile
      : {
          ...(analysed.profile || {}),
          summaryAnalysis: {
            ...(analysed.profile?.summaryAnalysis || {}),
            statement: "",
          },
        },
    projects: {
      ...(analysed.projects || {}),
      individualProjectsAnalysis: sanitizeArray(analysed.projects?.individualProjectsAnalysis, isMeaningfulProject),
    },
    workExperiences: {
      ...(analysed.workExperiences || {}),
      individualWorkExperiencesAnalysis: sanitizeArray(analysed.workExperiences?.individualWorkExperiencesAnalysis, isMeaningfulExperience),
    },
    educations: {
      ...(analysed.educations || {}),
      individualEducationsAnalysis: sanitizeArray(analysed.educations?.individualEducationsAnalysis, isMeaningfulEducation),
    },
    certifications: {
      ...(analysed.certifications || {}),
      individualCertificatesAnalysis: sanitizeArray(analysed.certifications?.individualCertificatesAnalysis, isMeaningfulCertificate),
    },
    languages: {
      ...(analysed.languages || {}),
      individualLanguagesAnalysis: sanitizeArray(analysed.languages?.individualLanguagesAnalysis, isMeaningfulLanguage),
    },
    awards: {
      ...(analysed.awards || {}),
      individualAwardsAnalysis: sanitizeArray(analysed.awards?.individualAwardsAnalysis, isMeaningfulAward),
    },
    skills: {
      ...(analysed.skills || {}),
      descriptionAnalysis: sanitizeArray(analysed.skills?.descriptionAnalysis, hasNonEmptyStatement),
    },
    custom: {
      ...(analysed.custom || {}),
      descriptionAnalysis: sanitizeArray(analysed.custom?.descriptionAnalysis, hasNonEmptyStatement),
    },
  };

  return sanitized as ResumeAnalysis;
};

export { analyseResumeForScoring };


